import { formatDistanceToNow } from 'date-fns';
import { User, Users2, Shield, MessageSquare, type LucideIcon } from 'lucide-react';

// Types
interface ActivityItem {
  id: string;
  type: 'user_joined' | 'squad_created' | 'report_filed' | 'message_flagged';
  title: string;
  description: string;
  timestamp: string;
}

interface RecentActivityProps {
  items: ActivityItem[];
  loading?: boolean;
  title?: string;
}

const iconMap: Record<ActivityItem['type'], { icon: LucideIcon; color: string }> = {
  user_joined: { icon: User, color: 'bg-blue-500/10 text-blue-400' },
  squad_created: { icon: Users2, color: 'bg-green-500/10 text-green-400' },
  report_filed: { icon: Shield, color: 'bg-red-500/10 text-red-400' },
  message_flagged: { icon: MessageSquare, color: 'bg-yellow-500/10 text-yellow-400' },
};

export function RecentActivity({
  items,
  loading = false,
  title = 'Recent Activity',
}: RecentActivityProps) {
  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-start gap-3 animate-pulse">
              <div className="w-10 h-10 rounded-lg bg-slate-800" />
              <div className="flex-1">
                <div className="h-4 w-32 bg-slate-800 rounded" />
                <div className="h-3 w-48 bg-slate-800 rounded mt-2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">No recent activity</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const { icon: Icon, color } = iconMap[item.type];
            return (
              <div key={item.id} className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{item.title}</p>
                  <p className="text-xs text-slate-400 truncate">{item.description}</p>
                </div>
                <span className="text-xs text-slate-500 whitespace-nowrap">
                  {formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Specific activity lists
interface RecentUsersProps {
  users: Array<{
    id: string;
    name: string;
    email: string;
    createdAt: string;
  }>;
  loading?: boolean;
}

export function RecentUsers({ users, loading = false }: RecentUsersProps) {
  const items: ActivityItem[] = users.map((user) => ({
    id: user.id,
    type: 'user_joined',
    title: user.name,
    description: user.email,
    timestamp: user.createdAt,
  }));

  return <RecentActivity items={items} loading={loading} title="Recent Users" />;
}

interface RecentSquadsProps {
  squads: Array<{
    id: string;
    name: string;
    memberCount: number;
    createdAt: string;
  }>;
  loading?: boolean;
}

export function RecentSquads({ squads, loading = false }: RecentSquadsProps) {
  const items: ActivityItem[] = squads.map((squad) => ({
    id: squad.id,
    type: 'squad_created',
    title: squad.name,
    description: `${squad.memberCount} members`,
    timestamp: squad.createdAt,
  }));

  return <RecentActivity items={items} loading={loading} title="Recent Squads" />;
}

interface RecentReportsProps {
  reports: Array<{
    id: string;
    reason: string;
    reportedBy: string;
    createdAt: string;
  }>;
  loading?: boolean;
}

export function RecentReports({ reports, loading = false }: RecentReportsProps) {
  const items: ActivityItem[] = reports.map((report) => ({
    id: report.id,
    type: 'report_filed',
    title: report.reason,
    description: `Reported by ${report.reportedBy}`,
    timestamp: report.createdAt,
  }));

  return <RecentActivity items={items} loading={loading} title="Recent Reports" />;
}
